import { Table, ActionIcon, Group, Text, Loader, Center, Box } from '@mantine/core';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  label: string;
  render?: (item: T) => ReactNode;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  getRowKey: (item: T) => string | number;
  emptyMessage?: string;
}

export function DataTable<T>({
  data,
  columns,
  loading = false,
  onEdit,
  onDelete,
  getRowKey,
  emptyMessage = 'Keine Einträge vorhanden',
}: DataTableProps<T>) {
  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (data.length === 0) {
    return (
      <Center py="xl">
        <Text c="vdzGray.6">{emptyMessage}</Text>
      </Center>
    );
  }

  return (
    <Box style={{ overflowX: 'auto' }}>
      <Table striped="even" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {columns.map((col) => (
              <Table.Th key={col.key}>{col.label}</Table.Th>
            ))}
            {(onEdit || onDelete) && <Table.Th style={{ width: 100 }}>Aktionen</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((item) => (
            <Table.Tr key={getRowKey(item)}>
              {columns.map((col) => (
                <Table.Td key={col.key}>
                  {col.render
                    ? col.render(item)
                    : String((item as Record<string, unknown>)[col.key] ?? '')}
                </Table.Td>
              ))}
              {(onEdit || onDelete) && (
                <Table.Td>
                  <Group gap="xs">
                    {onEdit && (
                      <ActionIcon
                        variant="outline"
                        color="vdzGray"
                        onClick={() => onEdit(item)}
                        title="Bearbeiten"
                      >
                        <IconPencil size={16} />
                      </ActionIcon>
                    )}
                    {onDelete && (
                      <ActionIcon
                        variant="outline"
                        color="red"
                        onClick={() => onDelete(item)}
                        title="Löschen"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}
